import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import AquillaButton from 'aquilla/core/molecules/Button';
import { commasIndianFormat } from '@utils/textHelper';
import { useEffect, useState } from 'react';
import pushRoute from '@utils/pushRoute';
import {
  navigateToCheckoutWithLearnerDetails,
  getCbtUserDetailsSubmittedEventProps
} from '@cont/SeeAll/utils';
import { useRouter } from 'next/router';
import useFetch from '@hooks/useFetch';
import { getAddonsMetaData } from '@cont/Checkout/apiHelper';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import usePricings from '@cont/Checkout/usePricings';
import {
  CHECKOUT_COMBO_GOAL_ADDED,
  CHECKOUT_COMBO_GOAL_REMOVED,
  CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
  CHECKOUT_CBT_USER_DETAILS_SUBMITTED
} from '@cont/Checkout/constants/segment';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import { plusProceedPaymentEventAnalytics } from '@cont/Checkout/utils';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import isEmptyObject from '@utils/isEmptyObject';
import BundleCbtVenueSelection from '@cont/Checkout/BundleCbtVenueSelection';
import useCbtBundleVenueReservation from '@cont/Checkout/useCbtBundleVenueReservation';
import {
  defaultSelectedPricing,
  discountedPricingIfAvailable
} from '../planSelectionHelper';
import AddonCard from '../BaseComponents/AddonCard';

const AddonsFlowWrapper = styled.div<{ $hasMultipleAddon: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
  margin-bottom: ${({ $hasMultipleAddon }) => $hasMultipleAddon && '150px'};
`;

const Title = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  width: 280px;
`;

const AddonCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
`;

const BottomCtaWrapper = styled.div<{ $hasMultipleAddon: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  padding-top: ${Spacings.SPACING_4B};
  ${({ $hasMultipleAddon }) => {
    if ($hasMultipleAddon)
      return `
        position: fixed;
        bottom: 0;
        padding-right: ${Spacings.SPACING_8B};
        background-color: var(--color-i-white);
        padding-bottom: ${Spacings.SPACING_4B};
        `;
  }}
`;

interface AddonsInfoType {
  iconicFeatures: Record<string, any>;
}

const keysToRemoveFromQuery = [
  'combo_subscription_uid',
  'is_combo',
  'parent_subscription_uid'
];

const getPriceBreakDown = (addOnData, referral) => {
  const { code, discountPercent } = referral ?? {};
  const { add_for, originally_for } = addOnData;
  const comboDiscount = Math.round(originally_for - add_for);
  const referralDiscount = Math.round((add_for * discountPercent) / 100);
  const priceBreakDown = [
    [
      {
        name: 'Original price',
        price: `₹${commasIndianFormat(originally_for)}`,
        type: 'positive'
      },
      {
        name: 'Combo discount',
        price: `-₹${commasIndianFormat(comboDiscount)}`,
        type: 'negative'
      }
    ]
  ];
  if (code)
    priceBreakDown.push([
      {
        name: 'Discounted price',
        price: `₹${commasIndianFormat(add_for)}`,
        type: 'positive'
      },
      {
        name: `‘${code}’ code applied`,
        price: `-₹${commasIndianFormat(referralDiscount)}`,
        type: 'negative'
      }
    ]);

  priceBreakDown.push([
    {
      name: 'Final price',
      price: `₹${commasIndianFormat(Math.round(add_for))}`,
      type: 'positive'
    }
  ]);
  return priceBreakDown;
};

const AddonsFlowPage = ({
  bundleAddOnData,
  purchaseDetails,
  discountedPricings,
  currentEventProps
}) => {
  const [addonBundleData, setAddonBundleData] = useState(bundleAddOnData);

  const { query } = useRouter();

  const {
    parent_subscription_uid,
    combo_subscription_uid,
    is_combo = false,
    goalUID = ''
  } = query;

  const { pricings } = usePricings({ goalUID });
  const { goal = {} } = pricings ?? {};
  const cbtConfig = goal?.cbtConfig;
  const { programmeUid, requiresVenueSelection, reserveVenue } =
    useCbtBundleVenueReservation(cbtConfig);
  const { isLoading: isMeDataLoading, isValidating: isMeDataValidating } =
    useMyInfo();
  const me = useMyInfo(getMe);

  const bundleAddonData =
    addonBundleData?.bundleAddOnsInfo?.bundle_add_ons ?? [];

  const [selectedAddon, setSelectedAddon] = useState<
    Record<string, any> | undefined
  >(bundleAddonData[0]);
  const [venueSelection, setVenueSelection] = useState(null);

  const goalList = bundleAddonData?.map((addOn) => addOn.uid);

  const [addonsMetaData, setAddonsMetaData] =
    useState<Array<Record<string, any> | undefined>>();

  useEffect(() => {
    (async () => {
      getAddonsMetaData(goalList).then((metaData) => {
        if (!metaData?.error) setAddonsMetaData(metaData);
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addonBundleData]);

  const { data: bundleAddOnsInfo, isLoading: fetchingBundleAddOns } = useFetch(
    parent_subscription_uid && combo_subscription_uid
      ? `v2/subscriptions/bundle_add_ons/details/?subscription_uid=${parent_subscription_uid}`
      : null,
    { useCamelCase: false },
    { shouldRetryOnError: false }
  );

  useEffect(() => {
    if (bundleAddOnsInfo) {
      if (addonBundleData?.bundleAddOnsInfo?.bundle_add_ons?.length > 0)
        setSelectedAddon(addonBundleData?.bundleAddOnsInfo?.bundle_add_ons[0]);
      setAddonBundleData({ bundleAddOnsInfo });
    }
  }, [addonBundleData?.bundleAddOnsInfo?.bundle_add_ons, bundleAddOnsInfo]);

  const hasMultipleAddon = bundleAddonData?.length > 1;
  const activeAddon = hasMultipleAddon ? selectedAddon : bundleAddonData[0];
  const showCbtVenueSelection =
    activeAddon && requiresVenueSelection(activeAddon);

  const { subscription: { referral = {} } = {} } = purchaseDetails ?? {};

  const { referral: { code: referralCode = '' } = {} } = selectedAddon ?? {};

  const isReferralAdded = !!referralCode;

  const eventProps = useCheckoutEventProps(
    goalUID,
    discountedPricingIfAvailable(
      parent_subscription_uid ?? selectedAddon?.subscription?.uid,
      isReferralAdded,
      discountedPricings
    ) || defaultSelectedPricing(selectedAddon?.subscription?.uid, pricings),
    true
  );

  const evtPayload = {
    ...eventProps,
    is_combo: true,
    goal_uid: goalUID,
    goal_name: selectedAddon?.name,
    referral_code: referralCode || 'NA',
    is_referral_successful: true,
    subscription_type: 'Combo',
    subscription_duration: selectedAddon?.subscription?.duration,
    subscription_price: selectedAddon?.originally_for,
    price: selectedAddon?.add_for
  };

  const isMeDataProcessing = isMeDataLoading && isMeDataValidating;

  useEffect(() => {
    if (
      !isMeDataProcessing &&
      parent_subscription_uid &&
      combo_subscription_uid
    )
      plusProceedPaymentEventAnalytics({
        ...evtPayload,
        ...me,
        userId: me?.uid,
        ...goal,
        user_auth: !isEmptyObject(me)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeDataProcessing]);

  const handleAddonPlanSubmit = (plan, learnerDetailsUid = null) => {
    if (learnerDetailsUid) {
      navigateToCheckoutWithLearnerDetails({
        goalSlug: selectedAddon?.slug || query.goalSlug,
        goalUid: selectedAddon?.uid || goalUID,
        subscriptionUid: plan ? plan?.uid : parent_subscription_uid,
        queryParams: {
          learner_details_uid: learnerDetailsUid,
          gUid: goalUID
        }
      });
      return;
    }

    const updatedQueryObject = is_combo
      ? Object.fromEntries(
          Object.entries(query).filter(
            ([key]) => !keysToRemoveFromQuery.includes(key)
          )
        )
      : query;

    pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
      query: {
        ...updatedQueryObject,
        subscriptionUID: plan ? plan?.uid : parent_subscription_uid
      }
    });
  };

  return (
    <>
      <AddonsFlowWrapper $hasMultipleAddon={hasMultipleAddon}>
        {bundleAddonData && (
          <Title variant="h2">
            Also prepare for{' '}
            {hasMultipleAddon
              ? 'these additional exams?'
              : `the ${bundleAddonData[0]?.name}`}
          </Title>
        )}
        <AddonCardWrapper>
          {addonBundleData?.bundleAddOnsInfo?.bundle_add_ons?.map(
            (addOnData) => {
              const addonsMetaInfo = addonsMetaData
                ? addonsMetaData?.find(
                    (metaData) => metaData?.goalUid === addOnData?.uid
                  )
                : {};
              const isChecked = addOnData?.uid === selectedAddon?.uid;
              const { add_for, originally_for } = addOnData;
              return (
                <AddonCard
                  key={addOnData?.uid}
                  onClick={() => {
                    setVenueSelection(null);
                    setSelectedAddon(addOnData);
                  }}
                  showRadioButton={hasMultipleAddon}
                  title={addOnData?.name}
                  isChecked={isChecked}
                  priceBreakDown={getPriceBreakDown(addOnData, referral)}
                  skuDetails={bundleAddOnData?.skuDetails}
                  comboDiscount={Math.round(originally_for - add_for)}
                  addonsMetaInfo={addonsMetaInfo as AddonsInfoType}
                />
              );
            }
          )}
        </AddonCardWrapper>
        {showCbtVenueSelection && (
          <BundleCbtVenueSelection
            programmeUid={programmeUid}
            activationDelayHours={goal?.cbtActivationDelayHours}
            isMobile
            onVenueChange={setVenueSelection}
          />
        )}
      </AddonsFlowWrapper>
      {addonBundleData && (
        <BottomCtaWrapper $hasMultipleAddon={hasMultipleAddon}>
          <AquillaButton
            variant="primary"
            size="large"
            fullWidth
            onClick={async () => {
              let learnerDetailsUid = null;

              if (showCbtVenueSelection) {
                if (!venueSelection) return;

                segment.track(
                  CHECKOUT_CBT_USER_DETAILS_SUBMITTED,
                  getCbtUserDetailsSubmittedEventProps({
                    venueSelection,
                    goalSegmentData: {
                      goal_uid: selectedAddon?.uid,
                      goal_name: selectedAddon?.name,
                      is_subscription_active: eventProps.is_subscription_active
                    },
                    entityProps: evtPayload,
                    programmeComposition: cbtConfig?.programmeComposition
                  }),
                  evtNameCategoryMap.BOTH
                );

                learnerDetailsUid = await reserveVenue(venueSelection);
                if (!learnerDetailsUid) return;
              }

              segment.track(CHECKOUT_COMBO_GOAL_ADDED, evtPayload);
              segment.track(CHECKOUT_PROCEED_TO_PAYMENT_SELECTED, evtPayload);
              handleAddonPlanSubmit(
                hasMultipleAddon
                  ? selectedAddon?.subscription
                  : bundleAddonData[0]?.subscription,
                learnerDetailsUid
              );
            }}
          >
            {hasMultipleAddon ? 'Add & continue to pay' : 'Add optional exam'}
          </AquillaButton>
          <AquillaButton
            variant="secondary"
            size="large"
            fullWidth
            onClick={() => {
              segment.track(CHECKOUT_COMBO_GOAL_REMOVED, evtPayload);
              if (currentEventProps)
                segment.track(
                  CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
                  currentEventProps
                );
              handleAddonPlanSubmit(bundleAddOnData?.skuDetails);
            }}
          >
            Continue without optional exam
          </AquillaButton>
        </BottomCtaWrapper>
      )}
    </>
  );
};

export default AddonsFlowPage;
